import React, { useState } from "react";
import { Link } from "react-router-dom";
import { LeadCard, ReasonAndTime, Sec } from "./parts";
import { nextCallView, rangeBothZones, reminderLine, lastTimeLine, toldUs, conversationView, noDash, untilWords, bookedVia } from "./leadModel.mjs";
import { shortDate, listWords, firstName } from "../format.mjs";

/*
 * CALL BOOKED: the call and its prep pack (the brief's Call booked row; matrix 1e:
 * "The call in both zones · join · reminders · prep pack · last time", "Attendees
 * in the prep pack", "Their ads in the prep pack"). Undrawn as a frame, so built
 * in the drawn card grammar on Gabriel's ruling (2026-10-01):
 *
 *   - THE CALL in their time and mine ("09:00–09:30 ET for Dana · 16:00–16:30
 *     your time"), with the calendar's join link when it gave one (never a guess:
 *     the watcher only captured links from Aug 2026). Booked through his booking
 *     page, it reads in the zone THEY picked there, says the agenda they chose,
 *     and links to the booking ("Open in Booking page →"), as the old booked-call
 *     box did (record.booking.next, leadModel.bookedVia);
 *   - ITS REMINDER PLAN, the pre-call reminders the automation has queued for this
 *     call, each in both zones with what it did, and Skip this call (the brief:
 *     "at Call booked that plan sits beside the call in the lead block"). The same
 *     plan is also listed in Behind the scenes;
 *   - THE PREP PACK: their words (the form, else their latest email), why they fit
 *     (the score's reason), the company in two lines, their LinkedIn ads when the
 *     auditor can see the account, who accepted the invite, and "Last time: reached
 *     Pricing sent, parked · no budget now" or "First time here" (only where stints
 *     exist, Feb 2026 on; before that nothing is said);
 *   - their time now.
 * With no future call on the calendar it says so; Next then carries "Invite sent?".
 *
 * PrepPack is exported for the meeting-today line (LeadBlock), which opens the same
 * pack in any other situation.
 *
 * PrepPack's own props: { record, call, withReason } (the reason row is left out
 * where the block already ends on "Why 80: …").
 *
 * Props:
 *   record   the shell's bundle: act (facts, ads, ads_line, auditor_connected),
 *            timeline.rows, automations.plans, company (properties, pipeline_stints,
 *            contacts), zones, score, contact
 *   ops      ops.toggleBreakdown, ops.call (skip the reminders), ops.reload
 */
export default function CallBooked({ record, ops }) {
  const facts = (record.act && record.act.facts) || {};
  const call = nextCallView({ facts, rows: record.timeline.rows, automations: record.automations, company: record.company, now: record.now });
  const via = bookedVia(call, record.booking, record.zones);
  const zones = (via && via.zones) || record.zones;
  return (
    <LeadCard label="The call" meta={call ? untilWords(call.at, { now: record.now }) : "nothing on the calendar"}>
      {call ? (
        <div>
          <div className="rec-headline">{call.title}</div>
          <div className="rec-body">
            {shortDate(call.at, { now: record.now })} · {rangeBothZones(call.at, call.end_at, zones)}
            {call.join && (
              <>
                {" · "}
                <a href={call.join} target="_blank" rel="noopener noreferrer">Join ↗</a>
              </>
            )}
          </div>
          {via && (
            <div className="rec-sub">
              Booked on your booking page{via.agenda ? ` · “${via.agenda}”` : ""} ·{" "}
              <Link className="rec-link" to="/booking-page">Open in Booking page →</Link>
            </div>
          )}
        </div>
      ) : (
        <div>
          <div className="rec-headline">No call on the calendar</div>
          <div className="rec-body">The stage says a call is booked, and no invite on the calendar backs it.</div>
        </div>
      )}
      {call && (call.plan || call.stopped) && <Reminders record={record} ops={ops} call={call} />}
      <div id="rec-prep">
        <PrepPack record={record} call={call} withReason={false} />
      </div>
      <ReasonAndTime record={record} ops={ops} mode="why" />
    </LeadCard>
  );
}

/*
 * The plan for THIS call. Skip is the automation's own route (AutomationCard.js),
 * answered for this one call only; a call already behind us, or a plan the company
 * switch stopped, has nothing left to skip.
 */
function Reminders({ record, ops, call }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const plan = call.plan || call.stopped;
  const skipped = plan.status === "skipped";
  const ahead = new Date(plan.call_at || call.at).getTime() > Date.now();
  const path = `/automations/${plan.automation_id}/calls/${encodeURIComponent(plan.event_id)}/skip`;
  const toggle = async () => {
    setBusy(true);
    setError(null);
    try {
      if (skipped) await ops.call(`${path}?company_id=${encodeURIComponent(plan.company_id)}`, { method: "DELETE" });
      else
        await ops.call(path, {
          method: "POST",
          body: JSON.stringify({ company_id: plan.company_id, contact_id: plan.contact_id, call_at: plan.call_at, call_title: plan.call_title }),
        });
      await ops.reload();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Sec>
      <span className="rec-lead__small">Reminders before the call</span>
      {skipped ? (
        <span className="rec-body">Skipped for this call{plan.stopped_reason ? ` · ${noDash(plan.stopped_reason)}` : ""}</span>
      ) : plan.status === "stopped" ? (
        <span className="rec-body">Stopped{plan.stopped_reason ? ` · ${noDash(plan.stopped_reason)}` : ""}</span>
      ) : (plan.reminders || []).length ? (
        (plan.reminders || []).map((r) => (
          <span key={r.step_index} className="rec-body">{reminderLine(r, record.zones, { now: record.now })}</span>
        ))
      ) : (
        <span className="rec-body">Nothing queued yet</span>
      )}
      {ahead && (plan.status === "active" || skipped) && (
        <span className="rec-inline">
          <button type="button" className="rec-link rec-lead__link" disabled={busy} onClick={toggle}>
            {busy ? "…" : skipped ? "Put the reminders back" : "Skip this call"}
          </button>
          {error && <span className="rec-err">{error}</span>}
        </span>
      )}
    </Sec>
  );
}

/**
 * THE PREP PACK: what to have read in the ninety seconds before the call. Every
 * row disappears when there is nothing behind it, and the pack with them.
 */
export function PrepPack({ record, call = null, withReason = true }) {
  const co = record.company || {};
  const act = record.act || {};
  const props = co.properties || {};
  const told = toldUs({ rows: record.timeline.rows, company: record.company, contact: record.contact, zones: record.zones, now: record.now });
  const convo = told.kind === "form" ? null : conversationView(record.timeline.rows, { company: record.company, contact: record.contact, zones: record.zones, now: record.now });
  const words = told.kind === "form" ? told.message : convo && convo.lead.direction === "inbound" ? convo.lead.body : null;
  const wordsFrom = told.kind === "form" ? "the form" : convo ? `${convo.lead.short}, ${shortDate(convo.lead.at, { now: record.now })}` : null;
  const description = props.company_description || props.description || null;
  const ads = act.ads ? act.ads_line || "an ad account is linked" : act.auditor_connected ? "no ad account in our auditor matches them" : null;
  const last = lastTimeLine(co);
  const reason = withReason && record.score && record.score.show && !record.score.blocked ? record.score.reason : null;
  // Who else is on the invite: the person on screen alone says nothing new.
  const me = firstName(record.contact);
  const others = call && call.attendees ? call.attendees.filter((n) => n !== me) : [];
  const attendees = others.length ? listWords(call.attendees) : null;
  if (!words && !description && !ads && !last && !reason && !attendees) return null;
  return (
    <Sec>
      <span className="rec-lead__small">Prep</span>
      <div className="rec-kv rec-kv--96">
        {words && (
          <>
            <span>Their words</span>
            <span className="rec-lead__clamp">
              {words}
              <span className="rec-meta"> · {wordsFrom}</span>
            </span>
          </>
        )}
        {reason && (
          <>
            <span>Why they fit</span>
            <span>{reason}</span>
          </>
        )}
        {description && (
          <>
            <span>Company</span>
            <span className="rec-lead__clamp">{description}</span>
          </>
        )}
        {ads && (
          <>
            <span>Their ads</span>
            <span>{ads}</span>
          </>
        )}
        {attendees && (
          <>
            <span>On the invite</span>
            <span>{attendees}</span>
          </>
        )}
      </div>
      {last && <span className="rec-body">{last}</span>}
    </Sec>
  );
}
